import React, { useEffect } from 'react';
import { X, Bell } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { NotificationItem } from './NotificationItem';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { Notification } from '@/hooks/useNotifications';

interface NotificationsScreenProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  isLoading: boolean;
  onNotificationClick: (notification: Notification) => Promise<void>;
  onMarkAllAsRead: () => void;
  isMarkingAllAsRead: boolean;
  unreadCount: number;
}

export const NotificationsScreen: React.FC<NotificationsScreenProps> = ({
  isOpen,
  onClose,
  notifications,
  isLoading,
  onNotificationClick,
  onMarkAllAsRead,
  isMarkingAllAsRead,
  unreadCount
}) => {
  const { t } = useI18n();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      // Cleanup: restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-20">
      <div className="bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-black" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('notifications.title', 'Notifications')}
            </h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-sm opacity-70 hover:opacity-100"
            aria-label="Close notifications"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
            </div>
          ) : notifications.length === 0 ? (
            <EmptyStateCard
              icon={<Bell className="w-16 h-16 text-gray-400" />}
              title={t('notifications.empty.title', "It's quiet in here!")}
              description={t(
                'notifications.empty.description',
                "You'll see notifications about your progress and community activity appear here."
              )}
            />
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={onNotificationClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <div className="text-center text-sm text-gray-500">
              {t('notifications.footer', 'All notifications are automatically marked as read when you open this screen.')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
